import { Loader2 } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Button } from "@/frontend/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import {
  useDeleteOwnPatientRecordsMutation,
  useDeletePatientMutation,
} from "@/frontend/mutations/dashboard/patients";
import type { DeletePatientDialogProps } from "@/frontend/types/dashboard/patients";
import { useWorkspacePersona } from "../individual/useWorkspacePersona";

/**
 * Confirmation for deleting one patient and every record retrieved for them,
 * shared by the page header menu and the Summary danger zone. An individual is
 * deleting their own records, so the copy says so and they land on Settings
 * afterwards, since the patients list would just provision a fresh record.
 */
export function DeletePatientDialog({
  patientMembershipId,
  patientName,
  open,
  onOpenChange,
}: DeletePatientDialogProps) {
  const navigate = useNavigate();
  const { isIndividual, isLoading: isResolvingPersona } = useWorkspacePersona();

  const deletePatient = useDeletePatientMutation(() => {
    toast.success("Patient deleted");
    navigate({ to: "/dashboard/patients", search: {} });
  });
  const deleteOwnRecords = useDeleteOwnPatientRecordsMutation(() => {
    toast.success("Your records were deleted");
    navigate({
      to: "/dashboard/settings",
      search: { section: "profile", destinationId: undefined, provider: "", webhookId: undefined },
    });
  });
  const deletion = isIndividual
    ? {
        mutation: deleteOwnRecords,
        title: "Delete your records?",
        description:
          "This permanently removes every record retrieved for you, across all connected health systems, and disconnects them. It cannot be undone.",
        button: "Delete my records",
      }
    : {
        mutation: deletePatient,
        title: "Delete this patient?",
        description: `This permanently removes ${patientName ?? "this patient"} and every record retrieved for them, across all connected health systems. It cannot be undone.`,
        button: "Delete patient",
      };
  const copy = isResolvingPersona
    ? {
        title: "Checking deletion options…",
        description: "Please wait while we confirm which data this action can remove.",
        button: "Checking…",
      }
    : deletion;
  const isDeleting = deletion.mutation.isPending;

  /**
   * Dispatches deletion after the workspace persona has resolved.
   * Individual owners use the identity-preserving records procedure.
   */
  const handleDelete = () => {
    if (isResolvingPersona) return;
    deletion.mutation.mutate({ patientMembershipId });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription className="text-xs">{copy.description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" className="h-9" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="h-9 gap-1.5"
            disabled={isResolvingPersona || isDeleting}
            onClick={handleDelete}
          >
            {isDeleting && <Loader2 className="size-4 animate-spin" />}
            {copy.button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
